import { Loader2Icon } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useChangeConfigFallback } from "@/services/admin/merchant-processors"
import type { MerchantProcessor } from "@/types/admin-merchant-processor"
import type { ProcessorConfigItem } from "@/types/admin-processor-config"

const prefix = "pages.adminProcessorConfig"

/** Métodos em que a contingência é REALMENTE consultada em runtime. */
const METHODS_THAT_READ_FALLBACK = ["CREDIT_CARD"]

export interface PendingFallbackChange {
  item: ProcessorConfigItem
  /** `null` = espelhar quem processa. */
  processor: MerchantProcessor | null
}

interface ChangeFallbackDialogProps {
  /** `null` mantém o diálogo fechado. */
  pending: PendingFallbackChange | null
  onClose: () => void
}

/**
 * Confirmação da troca de contingência.
 *
 * Confirma, e não salva direto, pela mesma razão da troca de processador: o operador precisa
 * reconhecer um clique errado antes que ele vire uma escrita auditada.
 *
 * O aviso sobre PIX e boleto não é detalhe: o campo existe para todos os métodos, mas só o retry
 * de smart routing de cartão o consulta. Sem dizer isso, o operador configura contingência de
 * boleto achando que ela vai valer.
 */
export const ChangeFallbackDialog = ({
  pending,
  onClose,
}: ChangeFallbackDialogProps) => {
  const { t } = useTranslation()
  const { mutate, isPending } =
    useChangeConfigFallback(onClose)

  const methodLabel = pending
    ? t(`${prefix}.methods.${pending.item.method}`, {
        defaultValue: pending.item.method,
      })
    : ""

  const isRead =
    pending !== null &&
    METHODS_THAT_READ_FALLBACK.includes(pending.item.method)

  const toLabel =
    pending?.processor === null
      ? t(`${prefix}.fallback.mirrorsPrincipal`)
      : (pending?.processor?.name ??
        pending?.processor?.id ??
        "")

  // A MESMA regra da célula: se a contingência atual já é quem processa, dizer o nome da
  // credencial aqui contradiria o combo, que mostra "a mesma que processa" — e é essa
  // confusão que separar os dois campos veio evitar.
  const current = pending?.item.fallbackProcessor
  const fromLabel =
    current == null
      ? t(`${prefix}.fallback.unresolved`)
      : current.id === pending?.item.processor?.id
        ? t(`${prefix}.fallback.mirrorsPrincipal`)
        : (current.name ?? current.id)

  return (
    <AlertDialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(`${prefix}.fallback.title`)}
          </AlertDialogTitle>
          {/* Tudo o que o operador precisa ouvir vive DENTRO da description: o Radix
              aponta o aria-describedby só para ela. */}
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                {t(`${prefix}.fallback.description`, {
                  method: methodLabel,
                  whiteLabel:
                    pending?.item.whiteLabelName ??
                    pending?.item.whiteLabelId ??
                    "",
                  from: fromLabel,
                  to: toLabel,
                })}
              </p>
              {/* O campo existe para todo método, mas só o cartão o lê. Deixar isso
                  implícito faria o operador confiar numa contingência que nunca roda. */}
              {!isRead && (
                <span className="block rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  {t(`${prefix}.fallback.notReadWarning`, {
                    method: methodLabel,
                  })}
                </span>
              )}
              <span className="block text-xs text-muted-foreground">
                {t(`${prefix}.changeProcessor.audited`)}
              </span>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {t(`${prefix}.changeProcessor.cancel`)}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={(event) => {
              // Sem isto o Radix fecha o diálogo no clique, e o operador perde o
              // retorno de erro da requisição que acabou de disparar.
              event.preventDefault()
              if (pending) {
                mutate({
                  configId: pending.item.id,
                  processorId:
                    pending.processor?.id ?? null,
                })
              }
            }}
          >
            {isPending && (
              <Loader2Icon
                className="mr-2 size-4 animate-spin"
                aria-hidden="true"
              />
            )}
            {isPending
              ? t(`${prefix}.fallback.confirming`)
              : t(`${prefix}.fallback.confirm`)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
